import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Divider,
  Dropdown,
  Grid,
  Header,
  Icon,
  Segment,
  Message,
} from "semantic-ui-react";

import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { updateDocInDb } from "../../../app/firestore/firestoreService";
import { isValidName } from "../../../app/common/util/util";
import { toast } from "react-toastify";

export default function DocArchiveLabelModal({ doc }) {
  const dispatch = useDispatch();
  const { docsTransArchived } = useSelector((state) => state.doc);
  const [textInput, setTextInput] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const labelOptions = useMemo(() => {
    const existingLabels = (docsTransArchived?.docs || [])
      .map((d) => d.archivedLabel)
      .filter(Boolean);
    const uniqueLabels = new Set(existingLabels);
    if (textInput.trim()) {
      uniqueLabels.add(textInput.trim());
    }
    return [...uniqueLabels]
      .sort((a, b) => a.localeCompare(b))
      .map((label) => ({
        key: label,
        value: label,
        text: label,
      }));
  }, [docsTransArchived, textInput]);

  function handleTextInputChange(value) {
    // Replace multiple consecutive spaces with single space
    value = value.replace(/\s+/g, ' ');
    setTextInput(value);

    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError("");
    }
  }

  async function handleSubmit() {
    // Validate the input
    if (!textInput.trim()) {
      setValidationError("Archive label cannot be empty");
      return;
    }

    if (!isValidName(textInput.trim())) {
      setValidationError("Label can only contain letters, numbers, underscores, parentheses, dashes, and spaces");
      return;
    }

    setIsSubmitting(true);
    try {
      await updateDocInDb(doc.id, {
        archived: true,
        archivedLabel: textInput.trim(),
        isFolder: false
      });
      toast.success("Document successfully archived with label");
      dispatch(
        closeModal({
          modalType: "DocArchiveLabelModal",
        })
      );
    } catch (error) {
      toast.error(error.message);
      setValidationError("Failed to archive document. Please try again.");
      setIsSubmitting(false);
    }
  }

  async function handleCreateFolder() {
    // Validate the input
    if (!textInput.trim()) {
      setValidationError("Folder label cannot be empty");
      return;
    }

    if (!isValidName(textInput.trim())) {
      setValidationError("Label can only contain letters, numbers, underscores, parentheses, dashes, and spaces");
      return;
    }

    setIsSubmitting(true);
    try {
      await updateDocInDb(doc.id, {
        archived: true,
        archivedLabel: textInput.trim(),
        isFolder: true
      });
      toast.success("Document successfully moved to folder");
      dispatch(
        closeModal({
          modalType: "DocArchiveLabelModal",
        })
      );
    } catch (error) {
      toast.error(error.message);
      setValidationError("Failed to create folder. Please try again.");
      setIsSubmitting(false);
    }
  }

  async function handleArchiveWithoutLabel() {
    setIsSubmitting(true);
    try {
      await updateDocInDb(doc.id, {
        archived: true,
        isFolder: false
      });
      toast.success("Document successfully archived");
      dispatch(
        closeModal({
          modalType: "DocArchiveLabelModal",
        })
      );
    } catch (error) {
      toast.error(error.message);
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <ModalWrapper size="small">
        <Segment>
          <Grid>
            <Grid.Column>
              <Header size="large" color="blue">
                Archive / Folders
              </Header>
              <Divider />
              <p>
                You can optionally add a label to organize this document.
                Documents with the same label will be grouped together.
              </p>
              <Dropdown
                fluid
                selection
                search
                allowAdditions
                additionLabel="Use new label: "
                placeholder="Select or type an archive label (optional)"
                options={labelOptions}
                value={textInput}
                onAddItem={(e, data) => handleTextInputChange(data.value)}
                onChange={(e, data) => handleTextInputChange(data.value)}
                error={!!validationError}
                disabled={isSubmitting}
              />
              {validationError && (
                <Message negative size="small">
                  {validationError}
                </Message>
              )}
              <Divider />
              <Grid>
                <Grid.Column width={8}>
                  <Button
                    fluid
                    color="blue"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !textInput.trim()}
                    loading={isSubmitting}
                  >
                    Archive with Label
                  </Button>
                </Grid.Column>
                <Grid.Column width={8}>
                  <Button
                    fluid
                    color="green"
                    icon
                    labelPosition="left"
                    onClick={handleCreateFolder}
                    disabled={isSubmitting || !textInput.trim()}
                    loading={isSubmitting}
                  >
                    <Icon name="folder" />
                     Folder with Label
                  </Button>
                </Grid.Column>
                <Grid.Column width={16}>
                  <Button
                    fluid
                    onClick={handleArchiveWithoutLabel}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    Archive without Label
                  </Button>
                </Grid.Column>
              </Grid>
            </Grid.Column>
          </Grid>
        </Segment>
      </ModalWrapper>
    </>
  );
}
